import React, { useEffect, useRef, useState } from 'react';
import { Database, Globe, BarChart3, Users, Cpu, AlertCircle, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { getDisplayLabel, getNodeColor } from './commonUtils';
import PowerBIIcon from '../../../assests/power-bi-icon.svg';
import { MetricsPopover } from './MetricsPopover';

const getNodeIcon = (type) => {
  switch (type) {
    case 'contract':
      return Database;
    case 'service':
      return Cpu;
    case 'dashboard':
      return BarChart3;
    case 'ml_model':
      return Database;
    case 'api':
      return Globe;
    case 'report':
      return Users;
    default:
      return Globe;
  }
};

const priority = {
  healthy: 1,
  warning: 2,
  critical: 3,
};

const getWorstStatus = (status1, status2) => {
  // Normalize to lowercase strings
  const s1 = (status1 || "").toLowerCase();
  const s2 = (status2 || "").toLowerCase();

  const p1 = priority[s1] || 0; // 0 = invalid
  const p2 = priority[s2] || 0;

  // If both are invalid or empty → treat as critical
  if (p1 === 0 && p2 === 0) return "critical";

  // Return the worse (higher priority)
  return p1 >= p2 ? s1 : s2;
};

const mergeSubscribedAssets = (existingAssets = [], nextAssets = [], parentName = '') => {
  const assetMap = new Map();

  [...existingAssets, ...nextAssets].forEach((asset) => {
    if (!asset?.data_asset_id) return;

    const prev = assetMap.get(asset.data_asset_id);

    if (!prev) {
      assetMap.set(asset.data_asset_id, {
        ...asset,
        product_name: asset?.product_name || parentName || null,
      });
      return;
    }

    assetMap.set(asset.data_asset_id, {
      ...prev,
      ...asset,
      status: getWorstStatus(prev.status, asset.status),
      product_name: prev?.product_name || asset?.product_name || parentName || null,
    });
  });

  return Array.from(assetMap.values());
};

export const UpstreamNetworkView = ({ 
  consumers, 
  contractName,
  contractStatus,
  contractProductId,
  contractSubscribedAssets = [],
  graphProductIds = new Set(),
  isFullScreen = false,
}) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1.2);
  const [pan, setPan] = useState({ x: 0, y: 420 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedNode, setSelectedNode] = useState(null);
  const [metricsVisible, setMetricsVisible] = useState(false);
  const [metricsNodeId, setMetricsNodeId] = useState(null);
  const [metricsNodeName, setMetricsNodeName] = useState('');
  const [metricsNodeDetails, setMetricsNodeDetails] = useState(null);

  const getNodeSubscribedAssets = (node) => {
    if (node?.id === 'contract') {
      return contractSubscribedAssets || [];
    }
    return node?.subscribedAssets || [];
  };

  // Convert consumer data to nodes and edges with proper hierarchy
  const { nodes, edges } = React.useMemo(() => {
    const nodeMap = new Map();
    const edgeList = [];

    // Add root contract node at the center top
    nodeMap.set('contract', {
      id: 'contract',
      name: contractName,
      type: 'contract',
      status: contractStatus,
      isDataPulseProduct: true,
      isPowerBIReport: false,
      subscribedAssets: contractSubscribedAssets,
      x: 400,
      y: -80,
      level: 0
    });

    // Calculate positions for hierarchical layout
    const levelHeight = 120;
    const nodeSpacing = 180;

    // First, collect all nodes by level
    const nodesByLevel= [[], [], []]; // Support up to 3 levels deep

    const collectNodes = (consumer, level, parentName = contractName) => {
      if (level < nodesByLevel.length) {
        nodesByLevel[level].push({
          ...consumer,
          _parentName: parentName,
        });
      }
      
      if (consumer.dependencies) {
        consumer.dependencies.forEach(dep => collectNodes(dep, level + 1, consumer.name));
      }
    };

    // Collect all consumers and their dependencies
    consumers.forEach(consumer => collectNodes(consumer, 0));

    // Position nodes level by level
    nodesByLevel.forEach((levelNodes, level) => {
      if (levelNodes.length === 0) return;

      const y = -(80 + (level + 1) * levelHeight);
      const totalWidth = (levelNodes.length - 1) * nodeSpacing;
      const startX = 400 - totalWidth / 2;

      levelNodes.forEach((consumer, index) => {
        const x = startX + index * nodeSpacing;
        const existingNode = nodeMap.get(consumer.id);
        const existingStatus = existingNode?.status;
        const parentName = consumer._parentName || contractName;
        const mergedSubscribedAssets = mergeSubscribedAssets(existingNode?.subscribedAssets || [], consumer.subscribedAssets || [], parentName);

        nodeMap.set(consumer.id, {
          id: consumer.id,
          name: consumer.name,
          type: consumer.type,
          status: getWorstStatus(existingStatus, consumer.status),
          isDataPulseProduct: consumer.isDataPulseProduct !== false,
          isPowerBIReport:
            existingNode?.isPowerBIReport === true ||
            consumer.isPowerBIReport === true,
          subscribedAssets: mergedSubscribedAssets,
          x,
          y,
          level: level + 1,
          parentId: level === 0 ? 'contract' : undefined
        });
      });
    });

    // Create edges based on the hierarchy
    const createEdges = (consumer, parentId) => {
      edgeList.push({ from: parentId, to: consumer.id });
      
      if (consumer.dependencies) {
        consumer.dependencies.forEach(dep => {
          createEdges(dep, consumer.id);
        });
      }
    };

    // Create edges from contract to first level consumers
    consumers.forEach(consumer => {
      createEdges(consumer, 'contract');
    });

    return { nodes: Array.from(nodeMap.values()), edges: edgeList };
  }, [consumers, contractName, contractStatus, contractSubscribedAssets]);

  const handleZoomIn = () => setZoom(prev => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev / 1.2, 0.3));
  const handleReset = () => {
    setZoom(1.2);
    setPan({ x: 0, y: 420 });
    setSelectedNode(null);
    setMetricsVisible(false);
  };

  const handleNodeClick = (node, event) => {
    event.stopPropagation();

    const resolvedMetricsNodeId = node.id === 'contract'
      ? (contractProductId || node.id)
      : node.id;

    setSelectedNode((prev) => (prev === node.id ? null : node.id));

    if (node.isPowerBIReport) {
      setMetricsVisible(false);
      return;
    }

    setMetricsNodeId(resolvedMetricsNodeId);
    setMetricsNodeName(node.name || node.id);
    setMetricsNodeDetails({
      ...node,
      subscribedAssets: getNodeSubscribedAssets(node),
    });
    setMetricsVisible(true);
  };

  const handleCloseMetrics = () => {
    setMetricsVisible(false);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleSvgMouseLeave = () => {
    handleMouseUp();
  };

  // Keep default metrics panel behavior for contract node
  useEffect(() => {
    const contractNode = nodes.find(n => n.id === 'contract');
    if (contractNode) {
      const resolvedMetricsNodeId = contractProductId || 'contract';
      setSelectedNode('contract');
      setMetricsNodeId(resolvedMetricsNodeId);
      setMetricsNodeName(contractNode.name || 'contract');
      setMetricsNodeDetails({
        ...contractNode,
        subscribedAssets: getNodeSubscribedAssets(contractNode),
      });
      setMetricsVisible(true);
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className={`bg-white rounded-lg border border-gray-200 relative overflow-hidden ${isFullScreen ? 'h-full' : 'h-[550px]'}`}
    >
      {/* Controls */}
      <div className="absolute top-4 right-4 z-10 flex gap-2">
        <button
          onClick={handleZoomIn}
          className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          title="Zoom In"
        >
          <ZoomIn className="  h-4 text-gray-600" />
        </button>
        <button
          onClick={handleZoomOut}
          className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          title="Zoom Out"
        >
          <ZoomOut className="  h-4 text-gray-600" />
        </button>
        <button
          onClick={handleReset}
          className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          title="Reset View"
        >
          <RotateCcw className="  h-4 text-gray-600" />
        </button>
      </div>

      {/* Network Visualization */}
      <svg
        ref={svgRef}
        className="w-full h-full cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleSvgMouseLeave}
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="4"
            markerHeight="3"
            refX="3.5"
            refY="1.5"
            orient="auto"
          >
            <polygon points="0 0, 4 1.5, 0 3" fill="#6B7280" />
          </marker>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          {/* Gradient for connection lines */}
          <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.8"/>
            <stop offset="100%" stopColor="#6B7280" stopOpacity="0.6"/>
          </linearGradient>
        </defs>

        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {/* Background grid for better visual reference */}
          <defs>
            <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#f3f4f6" strokeWidth="1" opacity="0.3"/>
            </pattern>
          </defs>
          <rect width="800" height="600" fill="url(#grid)" />

          {/* Level indicators */}
          {/* {[1, 2, 3].map(level => (
            <g key={level}>
              <line
                x1="50"
                y1={-(80 + level * 120)}
                x2="750"
                y2={-(80 + level * 120)}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray="5,5"
                opacity="0.5"
              />
              <text
                x="60"
                y={-(80 + level * 120 - 10)}
                className="text-xs fill-gray-400"
                style={{ fontSize: '10px' }}
              >
                Level {level}
              </text>
            </g>
          ))} */}

          {/* Edges with improved styling */}
          {edges.map((edge, index) => {
            const fromNode = nodes.find(n => n.id === edge.from);
            const toNode = nodes.find(n => n.id === edge.to);
            if (!fromNode || !toNode) return null;

            const isHighlighted = selectedNode && (selectedNode === edge.from || selectedNode === edge.to);
            const opacity = selectedNode && !isHighlighted ? 0.2 : 0.8;

            return (
              <g key={index}>
                {/* Connection line */}
                <line
                  x1={toNode.x}
                  y1={toNode.y + (toNode.type === 'contract' ? 30 : 25)}
                  x2={fromNode.x}
                  y2={fromNode.y - 25}
                  stroke={isHighlighted ? "#3B82F6" : "#9CA3AF"}
                  strokeWidth={isHighlighted ? "3" : "2"}
                  markerEnd="url(#arrowhead)"
                  opacity={opacity}
                  strokeDasharray={"4 6"} // {isHighlighted ? "0" : "4 6"}  // Dashed lines for normal, solid for highlight
                  className="transition-all duration-200 dotted-line"
                />
                
                {/* Data flow animation for highlighted connections */}
                {isHighlighted && (
                  <circle r="3" fill="#3B82F6" opacity="0.8">
                    <animateMotion
                      dur="2s"
                      repeatCount="indefinite"
                      path={`M ${toNode.x} ${toNode.y + 30 } L ${fromNode.x} ${fromNode.y - 25 }`}
                    />
                  </circle>
                )}
              </g>
            );
          })}

          {/* Nodes */}
          {nodes.map((node) => {
            const Icon = getNodeIcon(node.type);
            const color = getNodeColor(
              node.type,
              node.status,
              node.isDataPulseProduct,
              node.isPowerBIReport,
            );
            const isSelected = selectedNode === node.id;
            const isContract = node.type === 'contract';
            const isPowerBIReportNode = node.isPowerBIReport === true;
            const radius = isContract ? 30 : 25;
            const shouldBlink = node.name?.trim() === contractName.trim();
            const displayLabel = getDisplayLabel(node.name, isContract ? 22 : 15);
            return (
              <g
                key={node.id}
                onClick={(event) => handleNodeClick(node, event)}
              >
                <title>{node.name}</title>
                {/* Node shadow */}
                {!isPowerBIReportNode && (
                  <circle
                    cx={node.x + 2}
                    cy={node.y + 2}
                    r={radius}
                    fill="rgba(0,0,0,0.1)"
                    className="pointer-events-none"
                  />
                )}

                {isContract ?
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={radius}
                    fill={color}
                    stroke={isSelected ? '#3B82F6' : '#ffffff'}
                    strokeWidth={isSelected ? 4 : 3}
                    className="cursor-pointer transition-all duration-200 hover:stroke-blue-400 hover:stroke-4"
                    filter={isSelected ? 'url(#glow)' : undefined}
                  /> : 
                    isPowerBIReportNode ? (
                      <>
                        {isSelected && (
                          <rect
                            x={node.x - 22}
                            y={node.y - 22}
                            width="44"
                            height="44"
                            fill="transparent"
                            stroke="#3B82F6"
                            strokeWidth="2"
                            rx="6"
                          />
                        )}
                        <rect
                          x={node.x - 18}
                          y={node.y - 18}
                          width="36"
                          height="36"
                          fill="transparent"
                          stroke="#D4A017"
                          strokeWidth={isSelected ? 3 : 2.5}
                          rx="4"
                          className="cursor-pointer"
                        />
                      </>
                    ) : (
                    <rect
                    x={node.x - radius}
                    y={node.y - radius}
                    width={radius * 2}
                    height={radius * 2}
                    fill={color}
                    stroke={isSelected ? '#3B82F6' : '#ffffff'}
                    strokeWidth={isSelected ? 4 : 3}
                    className={`cursor-pointer transition-all duration-200 hover:stroke-blue-400 hover:stroke-4 ${shouldBlink ? "node-blink" : "" }`}
                    filter={isSelected ? 'url(#glow)' : undefined}
                  />
                    )
                }
                {/* Node icon */}
                <foreignObject
                  x={node.x - (isPowerBIReportNode ? 14 : 12)}
                  y={node.y - (isPowerBIReportNode ? 14 : 12)}
                  width={isPowerBIReportNode ? "28" : "24"}
                  height={isPowerBIReportNode ? "28" : "24"}
                  className="pointer-events-none"
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {node.isPowerBIReport ? (
                      <img
                        src={PowerBIIcon}
                        alt="Power BI"
                        style={{
                          width: isPowerBIReportNode ? '22px' : '16px',
                          height: isPowerBIReportNode ? '22px' : '16px',
                          objectFit: 'contain',
                          display: 'block',
                          filter: 'drop-shadow(0 2px 4px rgba(15, 23, 42, 0.20))',
                        }}
                      />
                    ) : (
                      <Icon style={{ width: '18px', height: '18px', color: '#ffffff', display: 'block' }} />
                    )}
                  </div>
                </foreignObject>

                {/* Status indicator for error nodes */}
                {node.status === 'error' && (
                  <g>
                    <circle
                      cx={node.x + radius - 8}
                      cy={node.y - radius + 8}
                      r="8"
                      fill="#EF4444"
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                    <foreignObject
                      x={node.x + radius - 12}
                      y={node.y - radius + 4}
                      width="8"
                      height="8"
                      className="pointer-events-none"
                    >
                      <AlertCircle className="w-2 h-2 text-white" />
                    </foreignObject>
                  </g>
                )}

                {/* Node label */}
                <text
                  x={node.x}
                  y={node.y + radius + 15}
                  textAnchor="middle"
                  className={`font-medium ${
                    isContract ? 'text-sm fill-gray-800' : 'text-xs fill-gray-700'
                  }`}
                  style={{ fontSize: isContract ? '12px' : '11px' }}
                >
                    {displayLabel}
                </text>

                {/* Type label */}
                {!isContract && node.name.trim() === contractName.trim() && (<text
                  x={node.x}
                  y={node.y + radius + (isContract ? 28 : 26)}
                  textAnchor="middle"
                  className="text-xs fill-gray-500"
                  style={{ fontSize: '9px' }}
                >
                  (Circular dependency)
                </text>)}

                {/* Level indicator for non-contract nodes */}
                {!isContract && (
                  <text
                    x={node.x - radius - 15}
                    y={node.y + 3}
                    textAnchor="middle"
                    className="text-xs fill-gray-400 font-mono"
                    style={{ fontSize: '8px' }}
                  >
                    L{node.level}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      <MetricsPopover
        key={metricsNodeId || 'metrics-panel'}
        visible={metricsVisible}
        nodeId={metricsNodeId}
        nodeName={metricsNodeName}
        nodeStatus={metricsNodeDetails?.status}
        isDataPulseProduct={metricsNodeDetails?.isDataPulseProduct}
        graphProductIds={graphProductIds}
        onClose={handleCloseMetrics}
      />

      {/* Legend */}
      {/* <div className="absolute bottom-4 left-4 bg-white border border-gray-300 rounded-lg p-3 shadow-sm">
        <div className="text-xs font-medium text-gray-700 mb-2">Legend</div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <div className="  h-4 bg-blue-500 rounded-full border-2 border-white"></div>
            <span className="text-blue-600">Data Contract</span>
          </div>
          <div className="flex gap-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-green-600">healthy</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span className="text-orange-600">warning</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-red-600">critical</span>
            </div>
          </div>
        </div>
      </div> */}

      {/* Instructions */}
      <div className="absolute bottom-4 right-4 bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs text-blue-700">
        <div className="font-medium mb-1">Instructions:</div>
        <div>• Click nodes to view details and highlight connections</div>
        <div>• Drag to pan • Use zoom controls</div>
      </div>
    </div>
  );
};